import React, { useCallback, useMemo, useState } from "react";
import Badge from "../../../../components/Atoms/Badge";
import Button from "../../../../components/Atoms/Button";
import { useNavigate } from "react-router-dom";
import { useSwal } from "../../../../components/Notifications/swal/SwalCom";
import { MSG, disconnectService, recheckServices } from "../../../../services/authProviders";
import { providerCatalog, useExportTargets, writeTargets } from "../../../../services/linkedServices";
import { getLoggedInOwnerId } from "../../../../utils/auth";

/**
 * Where a published deployment pack can be SENT — one row per target.
 *
 * A target is a site whose credentials this browser's session actually proved, so the
 * list is computed by the server (the cascade in 1_management_api) and cached; the
 * browser can read it and can never mint one. See the frozen contract §8.2/§8.3.
 *
 * Three rules that are the whole point of the screen, and each of which the obvious
 * implementation gets wrong:
 *
 *  1. A TARGET THE CREDENTIALS DID NOT MATCH IS ABSENT, NOT DISABLED. The server writes
 *     no target for a `no_account` / `refused` probe, and nothing here invents one from
 *     the provider catalogue. A greyed-out row for a site the person does not own is an
 *     invitation to go and find the password for it.
 *  2. NOTHING IS KEYED ON A PROVIDER NAME. Every label, monogram, colour, task gate and
 *     sentence comes out of `target.descriptor` (§8.1). A provider added to the backend
 *     registry renders correctly here with no change to this file — which is the only
 *     reason there is a descriptor at all. There is no `reason -> English` map: refusal
 *     and help copy are rendered verbatim as the server wrote them.
 *  3. THE PACK IS PROVIDER-AGNOSTIC AND STAYS SO. This list sits BESIDE the deployment
 *     pack card, never instead of it: the pack is one immutable artifact, and a target
 *     is one place it may go. Merging them would make the artifact look like it belonged
 *     to whichever provider happened to be connected.
 *
 * This file carries both the list and the row. The frozen contract names them as two
 * files (ExportTargetList.jsx / ExportTargetRow.jsx); this work item owns one, and the
 * row is small enough that splitting it across an ownership boundary would cost more
 * than it buys. The row's prop signature is the contract's, unchanged.
 *
 * GPORTAL's link is the only authority for an export (MODEL_BRIDGE_PLAN §10b), and its
 * `requires_approval` flag — which LabelFlow cannot read — decides whether a person sees
 * the request at all. Since 2026-09-11 the default is straight through (plan decision 8),
 * so no copy in this file may promise a human review.
 * A target here is a HINT ABOUT WHAT TO SHOW — a stale or wrong one costs a clear error
 * and can never cause an unauthorised deployment.
 */

// An unvetted variant would render `.lf-badge--<x>`, which has no CSS at all — i.e. an
// invisible chip. The six names below are the ones theme-overrides.css really styles.
const BADGE_VARIANTS = new Set(["neutral", "primary", "success", "warning", "danger", "info"]);

// Connecting a service is not done here. It needs the service's own credentials AND the
// actor's LabelFlow password, and that form already exists, once, on Settings -> Connected
// services (`src/pages/settings/`). A second copy on this screen would be a second place to
// keep a credential form correct, and the one that is edited second is the one that rots.
// So the button below is the affordance and that screen is the flow.
const CONNECT_ROUTE = "/ConnectedServices";

const accentStyle = (accentIndex) => {
  // A custom property pointing AT a token, not a colour literal: the row's accent has to
  // come from the descriptor (rule 2) and CSS cannot index a token by number. This is the
  // only inline style this feature is allowed.
  const n = Math.min(5, Math.max(1, Number(accentIndex) || 1));
  return { "--exptarget-accent": `var(--chart-${n})` };
};

const verifiedText = (iso) => {
  if (!iso) return "never verified";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "verified at an unknown time";
  return `verified ${at.toLocaleString()}`;
};

const errorText = (res) => {
  if (!res) return "Something went wrong. Try again.";
  if (res.message === MSG.UNAUTHORISED) return "That LabelFlow password is not right.";
  if (res.message === MSG.THROTTLED) return "Too many attempts. Wait a few minutes and try again.";
  if (res.message === MSG.NETWORK) return "Could not reach the server.";
  return res.detail || "Something went wrong. Try again.";
};

/**
 * The actor's own LabelFlow password, asked for inline.
 *
 * Re-checking and disconnecting are proved with the home password on every call
 * (contract SEC-6) and this app stores no password anywhere — so it has to be typed. It
 * is a panel rather than a dialog because it always answers a control the person just
 * pressed, three lines above it; a modal for one field reads as a second decision.
 */
const ActorPasswordPrompt = ({ title, note, confirmLabel, busy, error, onConfirm, onCancel }) => {
  const [password, setPassword] = useState("");

  return (
    <form
      className="exptargets__auth"
      onSubmit={(e) => {
        e.preventDefault();
        if (password) onConfirm(password);
      }}
    >
      <p className="exptargets__auth-title">{title}</p>
      {note && <p className="exptargets__auth-note">{note}</p>}
      <input
        type="password"
        className="form-control exptargets__auth-input"
        autoComplete="current-password"
        placeholder="Your LabelFlow password"
        value={password}
        disabled={busy}
        onChange={(e) => setPassword(e.target.value)}
      />
      {error && (
        <p className="exptargets__auth-error" role="alert">
          {error}
        </p>
      )}
      <div className="exptargets__auth-actions">
        <Button type="submit" variant="primary" size="sm" disabled={busy || !password}>
          {busy ? "Checking…" : confirmLabel}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

/**
 * One target. Every branch below is decided by the descriptor, never by `target.provider`.
 */
const ExportTargetRow = ({ target, modelType, packReady, onRecheck, onDisconnect, onExport }) => {
  const d = target.descriptor || {};
  const label = d.exportLabel || d.label || target.label || target.provider;
  const noun = d.exportNoun || "deployment";
  const accepts = Array.isArray(d.acceptsTasks) ? d.acceptsTasks : [];

  // A pack built for a task the target's runtime cannot run is refused over there, not
  // here — so say which task and why, in the provider's own words, instead of an error
  // after the fact. An empty acceptsTasks means the provider did not say; do not guess.
  const taskSupported = accepts.length === 0 || accepts.includes(modelType);
  const unusable = target.usable === false;
  const needsEntitlement = Boolean(d.requiresEntitlement) && target.entitlementState !== "entitled";

  const variant = BADGE_VARIANTS.has(d.badgeVariant) ? d.badgeVariant : "neutral";
  const rowClass = [
    "exptarget",
    target.stale ? "exptarget--stale" : "",
    unusable || !taskSupported ? "exptarget--unusable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={rowClass}>
      <span className="exptarget__mono" style={accentStyle(d.accentIndex)} aria-hidden="true">
        {(d.initials || label.slice(0, 2)).toUpperCase()}
      </span>

      <div className="exptarget__body">
        <div className="exptarget__name">
          {target.name || target.email || label}
          <Badge variant={variant}>{label}</Badge>
          {target.stale && <Badge variant="warning">Needs re-checking</Badge>}
        </div>
        <div className="exptarget__meta">
          {target.email && <span>{target.email}</span>}
          {target.externalId && (
            <code className="exptarget__id" title="Account id on the target platform">
              {target.externalId}
            </code>
          )}
          <span>{verifiedText(target.verifiedAt)}</span>
        </div>

        {!taskSupported && (
          <p className="exptarget__note">
            {(d.unsupportedTaskNote || "This target cannot run {task} models.").replace(
              "{task}",
              modelType || "this"
            )}
          </p>
        )}

        {taskSupported && unusable && (
          <p className="exptarget__note">
            This link has not been checked for too long to be used. Re-check it — you will be
            asked for your LabelFlow password.
          </p>
        )}

        {taskSupported && !unusable && !packReady && (
          <p className="exptarget__note">
            Publish the deployment pack above first. It is the checksummed artifact that gets
            validated and delivered, so there is nothing to send until it exists.
          </p>
        )}

        {taskSupported && !unusable && packReady && needsEntitlement && (
          <p className="exptarget__note">{d.entitlementHint}</p>
        )}
      </div>

      <div className="exptarget__actions">
        {taskSupported && !unusable && packReady && (
          // Rendered even when the site is not entitled yet: asking is the only way the
          // entitlement ever gets issued, and the hint above says who issues it. A control
          // that is hidden until somebody else acts is a feature nobody discovers.
          <Button
            variant="primary"
            size="sm"
            tooltipText={`Ask for this pack to be deployed as a ${noun}`}
            onClick={() => onExport(target)}
          >
            {`Export to ${label}`}
          </Button>
        )}
        {target.stale || unusable ? (
          <Button
            variant="outline-secondary"
            size="sm"
            tooltipText="Prove these credentials again"
            onClick={onRecheck}
          >
            Re-check
          </Button>
        ) : null}
        {/* Only a link somebody CONNECTED can be disconnected. A `signin` target IS the
            credentials that were just used, so a Disconnect on it would come straight back
            on the next sign-in — a dead control wearing a working one's clothes. */}
        {target.via === "connected" && (
          <Button
            variant="outline-danger"
            size="sm"
            tooltipText={d.disconnectNote || "Remove this link"}
            onClick={() => onDisconnect(target)}
          >
            Disconnect
          </Button>
        )}
      </div>
    </li>
  );
};

const ExportTargets = ({ modelType, packReady, onRequestExport }) => {
  // Defaults rather than a bare destructure: a corrupt cache must read as "no targets"
  // (SEC-17), never as an exception that takes the whole training-result page down.
  const { targets = [], providers = [], reload = () => {} } = useExportTargets() || {};
  const { swalConfirm, swalFail, swalSuccess } = useSwal();

  // { kind: 'recheck' | 'disconnect', target? } — which control is waiting on a password.
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const navigate = useNavigate();

  const catalog = useMemo(
    () => (providers && providers.length ? providers : providerCatalog() || []),
    [providers]
  );
  const connectable = useMemo(
    () => catalog.filter((p) => p && p.canExport && p.configured),
    [catalog]
  );
  const unavailable = useMemo(
    () => catalog.filter((p) => p && p.canExport && !p.configured),
    [catalog]
  );

  const adopt = useCallback(
    (data) => {
      // The server owns the list; the browser only ever caches what it was handed
      // (SEC-17). A reply without `targets` is a reply we do not understand — re-read
      // what is stored rather than writing a guess over it.
      if (data && Array.isArray(data.targets)) {
        writeTargets(data.targets, data.providers || []);
      }
      reload();
    },
    [reload]
  );

  const closePrompt = useCallback(() => {
    setPending(null);
    setAuthError("");
    setBusy(false);
  }, []);

  const submitActor = useCallback(
    async (actorPassword) => {
      const userId = getLoggedInOwnerId();
      if (!userId) {
        setAuthError("You are signed out — sign in again.");
        return;
      }
      setBusy(true);
      setAuthError("");
      const res =
        pending.kind === "disconnect"
          ? await disconnectService({
              userId,
              provider: pending.target.provider,
              actorPassword,
            })
          : await recheckServices({ userId, actorPassword });
      setBusy(false);
      if (!res || !res.ok) {
        setAuthError(errorText(res));
        return;
      }
      adopt(res.data);
      closePrompt();
      swalSuccess(
        "<b>Done</b>",
        pending.kind === "disconnect"
          ? "<p>The link has been removed.</p>"
          : "<p>Your linked sites have been checked again.</p>"
      );
    },
    [pending, adopt, closePrompt, swalSuccess]
  );

  const askDisconnect = useCallback(
    (target) => {
      const note = target.descriptor?.disconnectNote || "";
      swalConfirm(
        "<b>Disconnect this site?</b>",
        `<p>${target.name || target.email || target.label}</p>${note ? `<p>${note}</p>` : ""}`,
        (result) => {
          if (result?.isConfirmed) {
            setAuthError("");
            setPending({ kind: "disconnect", target });
          }
        }
      );
    },
    [swalConfirm]
  );

  const askRecheck = useCallback(() => {
    setAuthError("");
    setPending({ kind: "recheck" });
  }, []);

  const requestExport = useCallback(
    (target) => {
      const noun = target.descriptor?.exportNoun || "deployment";
      const label = target.descriptor?.exportLabel || target.label || target.provider;
      swalConfirm(
        `<b>Export to ${label}?</b>`,
        `<p>This asks for the published pack to be registered as a ${noun} for ` +
          `<b>${target.name || target.email || label}</b>.</p>` +
          // Whether a person sees this before it lands is a property of the LINK on
          // GPORTAL's side, and LabelFlow cannot read that table — it is the same
          // blind spot that makes entitlementState 'unknown'. Since 2026-09-11 the
          // default is straight through, so promising a human review would be wrong
          // for most sites; promising none would be wrong for the rest. What IS true
          // for every site is the validation gate and the fact that landing is not
          // running. (MODEL_BRIDGE_PLAN decision 8, taken 2026-09-11.)
          "<p>The pack is checked against the site before anything is delivered. " +
          "Depending on how this site's link is set up it may go straight through or " +
          "wait for a platform operator — and either way, accepting a pack is not " +
          "deploying it to a camera.</p>",
        (result) => {
          if (!result?.isConfirmed) return;
          if (typeof onRequestExport === "function") {
            onRequestExport(target);
            return;
          }
          // No silent failure: this build has no channel to send the request on — the
          // LabelFlow -> GPORTAL export-request call is made with a link token the browser
          // must never hold, and this deployment has no route in front of it yet. Say that,
          // and say what to do instead, rather than swallowing the press.
          swalFail(
            "<b>Cannot send the request yet</b>",
            "<p>This build cannot raise the export itself. Give your Gemmiz administrator " +
              `the pack fingerprint above and this site id — <b>${target.externalId || "unknown"}</b> — ` +
              "and they can raise it from the portal.</p>"
          );
        }
      );
    },
    [swalConfirm, swalFail, onRequestExport]
  );

  const hasTargets = targets.length > 0;

  return (
    <div className="exptargets">
      <div className="exptargets__head">
        <p className="exptargets__title">Where this pack can go</p>
        <p className="exptargets__sub">
          One row per site whose credentials you have proved. Nothing is delivered until the
          published pack has been validated on the far side.
        </p>
      </div>

      {hasTargets ? (
        <ul className="exptargets__list">
          {targets.map((t) => (
            <ExportTargetRow
              key={`${t.provider}:${t.externalId}`}
              target={t}
              modelType={modelType}
              packReady={packReady}
              onRecheck={askRecheck}
              onDisconnect={askDisconnect}
              onExport={requestExport}
            />
          ))}
        </ul>
      ) : (
        <div className="exptargets__empty">
          <p className="exptargets__empty-title">No export targets yet.</p>
          <p>
            A target is a site whose account you own. Signing in to LabelFlow with that account's
            own email and password links it by itself; if its password differs from your LabelFlow
            one, connect it under <b>Connected services</b> and it appears here.
          </p>
          {unavailable.map((p) => (
            <p key={p.key} className="exptargets__empty-note">
              {p.label} is not available on this deployment.
            </p>
          ))}
        </div>
      )}

      {/* The connect and re-check controls live under BOTH states: a person with one site
          usually has a second, and a stale row is re-checked from the same button. */}
      {pending ? (
        <ActorPasswordPrompt
          // Keyed so the field empties between two different uses of the prompt — a
          // password left in the box from the last press is one nobody meant to submit.
          key={pending.kind}
          title={
            pending.kind === "disconnect"
              ? "Confirm with your LabelFlow password"
              : "Re-check your linked sites"
          }
          note={
            pending.kind === "disconnect"
              ? "Removing a link is done as you, so it is proved with your own password."
              : "Every configured service is signed in to again with this password, and the list is rebuilt from what answers. A service whose own password is different has to be re-connected under Connected services instead."
          }
          confirmLabel={pending.kind === "disconnect" ? "Disconnect" : "Re-check"}
          busy={busy}
          error={authError}
          onConfirm={submitActor}
          onCancel={closePrompt}
        />
      ) : (
        <div className="exptargets__tools">
          {connectable.map((p) => (
            <Button
              key={p.key}
              variant="ghost"
              size="sm"
              tooltipText={p.connectHelp}
              onClick={() => navigate(CONNECT_ROUTE)}
            >
              {`Connect ${p.label}`}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            tooltipText="Check the linked sites again"
            onClick={askRecheck}
          >
            Re-check
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExportTargets;
